import { IReservation } from '@/interfaces/IReservation';
import { IPosTableView } from '@/interfaces/IPosTableView';
import { ITable } from '@/interfaces/ITable';
import type { IReservationBookingContext } from '@/interfaces/IReservationSettings';
import { IReservationInstant } from './reservation-time';

export type ITableReservationAvailabilityStatus =
  | 'free'
  | 'reserved'
  | 'occupied'
  | 'too_small';
export interface ITableReservationAvailability {
  table: ITable;
  status: ITableReservationAvailabilityStatus;
  selectable: boolean;
  requiresConfirmation: boolean;
  reservation: IReservation | null;
}
export interface ITableAvailabilityInput {
  tables: readonly (IPosTableView | ITable)[];
  reservations: readonly IReservation[];
  startsAt: IReservationInstant;
  durationMin: number;
  bufferMin: number;
  partySize: number;
  now: IReservationInstant;
  mode?: 'reservation' | 'now';
  excludeReservationId?: number;
}

export function requiresForcedTableSelection(
  selected: readonly ITableReservationAvailability[],
  partySize: number,
  tableGroups: IReservationBookingContext['tableGroups']
): boolean {
  if (!selected.length) return false;
  const ids = selected.map((choice) => choice.table.id);
  const matchesGroup = tableGroups.some(
    (group) =>
      group.tableIds.length === ids.length &&
      group.tableIds.every((id) => ids.includes(id)) &&
      group.minParty <= partySize &&
      partySize <= group.maxParty
  );
  return (
    selected.some((choice) => choice.status === 'reserved') ||
    selected.reduce((sum, choice) => sum + choice.table.capacity, 0) <
      partySize ||
    (selected.length > 1 && !matchesGroup)
  );
}

export function computeTableAvailability(
  input: ITableAvailabilityInput
): ITableReservationAvailability[] {
  const start = new Date(input.startsAt).getTime();
  const end = start + (input.durationMin + input.bufferMin) * 60000;
  const now = new Date(input.now).getTime();
  const live = input.mode === 'now' || (start <= now && now < end);
  const reservations = input.reservations.filter(
    (value) =>
      value.id !== input.excludeReservationId &&
      ['pending_payment', 'pending', 'confirmed', 'arrived', 'seated'].includes(
        value.status
      )
  );
  return input.tables
    .filter((table) => table.enabled && table.reservationsMode !== 'none')
    .map((table) => {
      const holds = reservations
        .filter((value) => value.tableIds.includes(table.id))
        .sort(
          (a, b) =>
            new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime() ||
            a.id - b.id
        );
      const seated = holds.find(
        (value) => value.status === 'seated' && value.sessionStatus === 1
      );
      const hintSeated =
        'reservation' in table &&
        table.reservation?.status === 'seated' &&
        table.reservation.id !== input.excludeReservationId;
      const occupied =
        live &&
        (('orders' in table && table.orders.length > 0) ||
          !!seated ||
          hintSeated);
      const reservation =
        holds.find(
          (value) =>
            start <
              new Date(value.endsAt).getTime() + input.bufferMin * 60000 &&
            new Date(value.startsAt).getTime() < end
        ) ?? null;
      const status: ITableReservationAvailabilityStatus = occupied
        ? 'occupied'
        : reservation
        ? 'reserved'
        : table.capacity < input.partySize
        ? 'too_small'
        : 'free';
      return {
        table,
        status,
        selectable: !occupied,
        requiresConfirmation: status === 'reserved',
        reservation: occupied ? seated ?? reservation : reservation,
      };
    });
}
